import { DESIGN_TOKENS, FONT_CONFIG } from '@myplayer/ui';

export type FirebaseEmailTemplateId =
  'VERIFY_EMAIL' | 'PASSWORD_RESET' | 'EMAIL_SIGNIN' | 'EMAIL_CHANGE';

export interface BrandedFirebaseEmailTemplate {
  readonly id: FirebaseEmailTemplateId;
  readonly consoleTemplateName: string;
  readonly subject: string;
  readonly preheader: string;
  readonly heading: string;
  readonly bodyIntro: string;
  readonly ctaLabel: string;
  readonly expiryAndSafetyNote: string;
  readonly actionUrlPath: string;
  readonly html: string;
}

export interface FirebaseAuthConsoleProvisioningItem {
  readonly id: string;
  readonly consoleLocation: string;
  readonly settingName: string;
  readonly requiredValue: string;
  readonly provisioningNotes: string;
}

export interface RenderBrandedEmailOptions {
  readonly heading: string;
  readonly preheader: string;
  readonly bodyIntro: string;
  readonly ctaLabel: string;
  readonly actionLinkPlaceholder?: string;
  readonly recipientEmailPlaceholder?: string;
  readonly expiryAndSafetyNote: string;
}

/**
 * Renders a branded Firebase Auth HTML email template using the shared
 * `@myplayer/ui` design tokens (Montserrat font, accent `#FF5A1F`, MyPlayer logo,
 * and plain-language copy).
 */
export function renderBrandedFirebaseAuthEmailHtml(options: RenderBrandedEmailOptions): string {
  const {
    heading,
    preheader,
    bodyIntro,
    ctaLabel,
    actionLinkPlaceholder = '%LINK%',
    recipientEmailPlaceholder = '%EMAIL%',
    expiryAndSafetyNote,
  } = options;

  const fontName = FONT_CONFIG.name;
  const fontCssFamily = FONT_CONFIG.cssFamily;
  const googleFontUrl = FONT_CONFIG.googleFontUrl;
  const brandColor = DESIGN_TOKENS.colors.brand;
  const brandContrast = DESIGN_TOKENS.colors.brandContrast;
  const pageBg = DESIGN_TOKENS.colors.pageBackground;
  const surfaceBg = DESIGN_TOKENS.colors.surface;
  const borderColor = DESIGN_TOKENS.colors.border;
  const textPrimary = DESIGN_TOKENS.colors.textPrimary;
  const textSecondary = DESIGN_TOKENS.colors.textSecondary;
  const textMuted = DESIGN_TOKENS.colors.textMuted;
  const logoText = DESIGN_TOKENS.brandIdentity.logoText;
  const logoSubtitle = DESIGN_TOKENS.brandIdentity.logoSubtitle;

  return [
    '<!DOCTYPE html>',
    '<html lang="en">',
    '<head>',
    '  <meta charset="utf-8" />',
    '  <meta name="viewport" content="width=device-width, initial-scale=1.0" />',
    `  <title>${heading} — ${logoText}</title>`,
    `  <link rel="stylesheet" href="${googleFontUrl}" />`,
    '</head>',
    `<body style="margin:0;padding:0;background-color:${pageBg};font-family:${fontCssFamily};color:${textPrimary};">`,
    `  <div style="display:none;max-height:0;overflow:hidden;opacity:0;">${preheader}</div>`,
    '  <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="padding:32px 16px;">',
    '    <tr>',
    '      <td align="center">',
    `        <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="max-width:520px;background-color:${surfaceBg};border:1px solid ${borderColor};border-radius:12px;overflow:hidden;">`,
    '          <tr>',
    `            <td style="padding:20px 28px;border-bottom:1px solid ${borderColor};">`,
    '              <table role="presentation" cellspacing="0" cellpadding="0">',
    '                <tr>',
    `                  <td style="width:32px;height:32px;border-radius:8px;background-color:${brandColor};text-align:center;vertical-align:middle;font-weight:700;font-size:15px;color:${brandContrast};">MP</td>`,
    '                  <td style="padding-left:12px;">',
    `                    <div style="font-family:${fontCssFamily};font-size:16px;font-weight:700;color:${textPrimary};line-height:1.2;">${logoText}</div>`,
    `                    <div style="font-family:${fontCssFamily};font-size:11px;font-weight:500;color:${textSecondary};line-height:1.2;">${logoSubtitle}</div>`,
    '                  </td>',
    '                </tr>',
    '              </table>',
    '            </td>',
    '          </tr>',
    '          <tr>',
    '            <td style="padding:28px;">',
    `              <h1 style="margin:0 0 12px 0;font-family:${fontCssFamily};font-size:20px;font-weight:600;color:${textPrimary};">${heading}</h1>`,
    `              <p style="margin:0 0 16px 0;font-family:${fontCssFamily};font-size:14px;line-height:1.6;color:${textSecondary};">${bodyIntro}</p>`,
    `              <p style="margin:0 0 24px 0;font-family:${fontCssFamily};font-size:13px;line-height:1.5;color:${textSecondary};">Account: <strong style="color:${textPrimary};">${recipientEmailPlaceholder}</strong></p>`,
    '              <table role="presentation" cellspacing="0" cellpadding="0" style="margin:0 0 24px 0;">',
    '                <tr>',
    `                  <td align="center" style="border-radius:6px;background-color:${brandColor};">`,
    `                    <a href="${actionLinkPlaceholder}" target="_blank" rel="noopener noreferrer" style="display:inline-block;padding:12px 24px;font-family:${fontCssFamily};font-size:14px;font-weight:600;color:${brandContrast};text-decoration:none;border-radius:6px;">${ctaLabel}</a>`,
    '                  </td>',
    '                </tr>',
    '              </table>',
    `              <p style="margin:0 0 12px 0;font-family:${fontCssFamily};font-size:12px;line-height:1.5;color:${textMuted};">${expiryAndSafetyNote}</p>`,
    `              <p style="margin:0;font-family:${fontCssFamily};font-size:11px;line-height:1.5;color:${textMuted};word-break:break-all;">If the button above does not open, copy and paste this link into your browser:<br /><a href="${actionLinkPlaceholder}" style="color:${brandColor};text-decoration:underline;">${actionLinkPlaceholder}</a></p>`,
    '            </td>',
    '          </tr>',
    '          <tr>',
    `            <td style="padding:16px 28px;border-top:1px solid ${borderColor};font-family:${fontCssFamily};font-size:11px;color:${textMuted};">`,
    `              Sent by ${logoText} ${logoSubtitle} (${fontName} brand template).`,
    '            </td>',
    '          </tr>',
    '        </table>',
    '      </td>',
    '    </tr>',
    '  </table>',
    '</body>',
    '</html>',
  ].join('\n');
}

export function getBrandedAuthEmailTemplates(): readonly BrandedFirebaseEmailTemplate[] {
  const logoText = DESIGN_TOKENS.brandIdentity.logoText;
  return [
    {
      id: 'VERIFY_EMAIL',
      consoleTemplateName: 'Email address verification',
      subject: `Verify your ${logoText} Admin Console email address`,
      preheader: `Confirm your work email address to finish setting up your ${logoText} account.`,
      heading: 'Verify your work email address',
      bodyIntro: `Welcome to the ${logoText} Admin Console. Confirm your email address below so you can manage products, QR codes, and workspace routing.`,
      ctaLabel: 'Verify email address',
      expiryAndSafetyNote:
        'This verification link expires in 24 hours. If you did not create a MyPlayer account, you can safely ignore this email.',
      actionUrlPath: '/auth/action?mode=verifyEmail',
      html: renderBrandedFirebaseAuthEmailHtml({
        heading: 'Verify your work email address',
        preheader: `Confirm your work email address to finish setting up your ${logoText} account.`,
        bodyIntro: `Welcome to the ${logoText} Admin Console. Confirm your email address below so you can manage products, QR codes, and workspace routing.`,
        ctaLabel: 'Verify email address',
        expiryAndSafetyNote:
          'This verification link expires in 24 hours. If you did not create a MyPlayer account, you can safely ignore this email.',
      }),
    },
    {
      id: 'PASSWORD_RESET',
      consoleTemplateName: 'Password reset',
      subject: `Reset your ${logoText} Admin Console password`,
      preheader: `Use this link to choose a new password for your ${logoText} account.`,
      heading: 'Reset your password',
      bodyIntro: `We received a request to reset the password for your ${logoText} Admin Console account. Click the button below to choose a new password.`,
      ctaLabel: 'Choose a new password',
      expiryAndSafetyNote:
        'This password reset link expires in 1 hour and can only be used once. If you did not ask to reset your password, you can ignore this email—your password has not changed.',
      actionUrlPath: '/auth/action?mode=resetPassword',
      html: renderBrandedFirebaseAuthEmailHtml({
        heading: 'Reset your password',
        preheader: `Use this link to choose a new password for your ${logoText} account.`,
        bodyIntro: `We received a request to reset the password for your ${logoText} Admin Console account. Click the button below to choose a new password.`,
        ctaLabel: 'Choose a new password',
        expiryAndSafetyNote:
          'This password reset link expires in 1 hour and can only be used once. If you did not ask to reset your password, you can ignore this email—your password has not changed.',
      }),
    },
    {
      id: 'EMAIL_SIGNIN',
      consoleTemplateName: 'Email link sign-in',
      subject: `Your sign-in link for ${logoText} Admin Console`,
      preheader: `Sign in to ${logoText} with one click—no password needed.`,
      heading: 'Sign in to Admin Console',
      bodyIntro: `Click the button below to sign in to your ${logoText} Admin Console workspace directly. No password is required.`,
      ctaLabel: 'Sign in to Admin Console',
      expiryAndSafetyNote:
        'This one-time sign-in link expires shortly and can only be used once. If you did not request this link, you can safely ignore this email.',
      actionUrlPath: '/auth/action?mode=signIn',
      html: renderBrandedFirebaseAuthEmailHtml({
        heading: 'Sign in to Admin Console',
        preheader: `Sign in to ${logoText} with one click—no password needed.`,
        bodyIntro: `Click the button below to sign in to your ${logoText} Admin Console workspace directly. No password is required.`,
        ctaLabel: 'Sign in to Admin Console',
        expiryAndSafetyNote:
          'This one-time sign-in link expires shortly and can only be used once. If you did not request this link, you can safely ignore this email.',
      }),
    },
    {
      id: 'EMAIL_CHANGE',
      consoleTemplateName: 'Email address change',
      subject: `Your ${logoText} Admin Console email address was updated`,
      preheader: `Security notice for your ${logoText} account email change.`,
      heading: 'Email address updated',
      bodyIntro: `The sign-in email address for your ${logoText} Admin Console account was recently changed. If you made this change, no further action is needed.`,
      ctaLabel: 'Restore original email',
      expiryAndSafetyNote:
        'If you did not change your email address, click the button above immediately to restore your original email and secure your account.',
      actionUrlPath: '/auth/action?mode=recoverEmail',
      html: renderBrandedFirebaseAuthEmailHtml({
        heading: 'Email address updated',
        preheader: `Security notice for your ${logoText} account email change.`,
        bodyIntro: `The sign-in email address for your ${logoText} Admin Console account was recently changed. If you made this change, no further action is needed.`,
        ctaLabel: 'Restore original email',
        expiryAndSafetyNote:
          'If you did not change your email address, click the button above immediately to restore your original email and secure your account.',
      }),
    },
  ];
}

/**
 * Checklist of Firebase Console / GCP Identity Platform settings required by
 * the provisioning agent so hosted email templates, custom action URLs, SMTP,
 * and OAuth brand screens match the MyPlayer Admin Console.
 */
export const FIREBASE_AUTH_CONSOLE_PROVISIONING_CHECKLIST: readonly FirebaseAuthConsoleProvisioningItem[] =
  [
    {
      id: 'custom-action-url',
      consoleLocation:
        'Firebase Console → Authentication → Templates → (All templates) → Customize action URL',
      settingName: 'Custom Action Handler URL',
      requiredValue: 'https://<ADMIN_HOST>/auth/action',
      provisioningNotes:
        'Point all Firebase email actions (resetPassword, verifyEmail, signIn, recoverEmail) to the Admin Console /auth/action route instead of the default <project>.firebaseapp.com/__/auth/action handler.',
    },
    {
      id: 'email-verification-template',
      consoleLocation: 'Firebase Console → Authentication → Templates → Email address verification',
      settingName: 'Verification Email Subject, Sender & HTML Body',
      requiredValue:
        'Sender name: "MyPlayer" · Reply-To: support@myplayr.co.uk · Subject: "Verify your MyPlayer Admin Console email address" · Body: Branded HTML from getBrandedAuthEmailTemplates() (VERIFY_EMAIL)',
      provisioningNotes: `Paste the VERIFY_EMAIL HTML template (uses ${FONT_CONFIG.name}, brand CTA button, MyPlayer header, and %LINK% / %EMAIL% placeholders). Note: Firebase Console only allows full HTML body editing when custom SMTP or Identity Platform template customization is enabled.`,
    },
    {
      id: 'password-reset-template',
      consoleLocation: 'Firebase Console → Authentication → Templates → Password reset',
      settingName: 'Password Reset Subject, Sender & HTML Body',
      requiredValue:
        'Sender name: "MyPlayer" · Reply-To: support@myplayr.co.uk · Subject: "Reset your MyPlayer Admin Console password" · Body: Branded HTML from getBrandedAuthEmailTemplates() (PASSWORD_RESET)',
      provisioningNotes:
        'Paste the PASSWORD_RESET HTML template with %LINK% and %EMAIL% placeholders and set the action URL to https://<ADMIN_HOST>/auth/action.',
    },
    {
      id: 'email-link-signin-template',
      consoleLocation: 'Firebase Console → Authentication → Templates → Email link sign-in',
      settingName: 'Passwordless Email Link Subject & Sender',
      requiredValue:
        'Sender name: "MyPlayer" · Reply-To: support@myplayr.co.uk · Subject: "Your sign-in link for MyPlayer Admin Console"',
      provisioningNotes:
        'Enable "Email link (passwordless sign-in)" under Sign-in method → Email/Password and apply the EMAIL_SIGNIN template.',
    },
    {
      id: 'custom-smtp-domain-dkim',
      consoleLocation: 'Firebase Console → Authentication → Templates → SMTP settings',
      settingName: 'Custom SMTP Server & Verified Sender Domain (SPF / DKIM / DMARC)',
      requiredValue:
        'Sender address: no-reply@myplayr.co.uk · SMTP host/port/credentials from Secret Manager',
      provisioningNotes:
        'Required by Firebase to unlock custom HTML email templates and send from @myplayr.co.uk instead of noreply@<project>.firebaseapp.com.',
    },
    {
      id: 'authorized-domains',
      consoleLocation: 'Firebase Console → Authentication → Settings → Authorized domains',
      settingName: 'Authorized Redirect & Action Domains',
      requiredValue: 'admin.myplayr.co.uk, staging-admin.myplayr.co.uk, localhost',
      provisioningNotes:
        'Must include every Admin Console domain so sendSignInLinkToEmail, sendPasswordResetEmail, sendEmailVerification, and Google OAuth popups are permitted.',
    },
    {
      id: 'public-facing-project-brand',
      consoleLocation:
        'Firebase Console → Project settings → General → Public settings & GCP Console → APIs & Services → OAuth consent screen',
      settingName: 'Public-facing App Name, Support Email & Brand Logo',
      requiredValue:
        'Public name: "MyPlayer" · Support email: support@myplayr.co.uk · App logo: MyPlayer emblem',
      provisioningNotes:
        'Populates %APP_NAME% in Firebase emails and brands the Google Sign-In OAuth consent prompt.',
    },
  ] as const;
